import { withEntityTitle } from '@/components/workflows/docProvenance';
import { sealBlockHtml, SEAL_STYLE_CSS, type SealContext } from '@/components/workflows/sealModel';
import { ENCUMBRANCE_LINES, type GivingWorkflowData } from './givingContent';
import type { SigningMode } from '../signingModel';

/**
 * DRAFT document builders for the "Give Something Into Stewardship" workflow.
 *
 * Every paper here is produced as a clearly marked DRAFT and waits on approval
 * before this workflow goes live. Plain spoken register, no dashes, no internal
 * codes, and no statements about tax outcomes.
 */

export const GIVING_FORMATS_APPROVED = true;

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function longDate(value?: string): string {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return safe.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BASE_STYLE = `
  @page { size: letter; margin: 1in; }
  body { font-family: Cambria, Georgia, serif; font-size: 12pt; line-height: 1.6; color: #1a1a1a; }
  h1 { font-size: 16pt; text-align: center; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 26px; }
  .subtitle { text-align: center; font-style: italic; margin-bottom: 26px; }
  .draft { border: 1px solid #8a6d3b; background: #fdf6e3; color: #6b5320; padding: 10px 14px; margin-bottom: 22px; font-size: 10pt; text-align: center; letter-spacing: 0.04em; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11pt; }
  th, td { border: 1px solid #bbb; padding: 8px 10px; text-align: left; vertical-align: top; }
  th { background: #f3f1ec; font-weight: bold; }
  .sig { margin-top: 46px; }
  .sig-line { border-bottom: 1px solid #333; width: 320px; height: 34px; margin-bottom: 4px; }
  .sig-label { font-size: 10pt; }
  ol li, ul li { margin-bottom: 8px; }
  .note { font-size: 10pt; font-style: italic; color: #444; margin-top: 26px; }
  ${SEAL_STYLE_CSS}
`;

function page(title: string, subtitle: string, inner: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>${esc(title)}</title><style>${BASE_STYLE}</style></head>
<body>
  <div class="draft">DRAFT format. This wording has not yet been approved. Do not sign this page until the approved format is in place.</div>
  <h1>${esc(title)}</h1>
  <p class="subtitle">${esc(subtitle)}</p>
  ${inner}
  <p class="note">Papers sign before anything moves. The date on this page is the true date it is signed.</p>
</body>
</html>`;
}


/** Renders the seal only when it is actually applied. Otherwise nothing at all. */
function sealSpot(ctx: SealContext): string {
  return sealBlockHtml(ctx);
}

export interface GivingBuildContext {
  data: GivingWorkflowData;
  associationName: string;
  associationOfficerRole: string;
  date?: string;
  /** True when the Association keeps a seal and the steward applied it. */
  sealEnabled?: boolean;
  /** The rendered seal markup. */
  sealSvg?: string;
}

export interface GivingOutput {
  key: string;
  title: string;
  html: string;
  requiresSignature: boolean;
  signing: SigningMode;
  defaultInk?: boolean;
  signingReason?: string;
}

function receiver(ctx: GivingBuildContext): string {
  return esc(ctx.data.receiving_entity || ctx.associationName || '[Receiving entity]');
}

function giver(ctx: GivingBuildContext): string {
  return esc(ctx.data.giver_name || '[Name of the person giving]');
}

function sigBlock(label: string, second?: string): string {
  return `<div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(label)}</div>
    ${second ? `<div class="sig-line" style="margin-top:34px"></div><div class="sig-label">${esc(second)}</div>` : ''}
    <div class="sig-line" style="margin-top:34px; width:200px"></div>
    <div class="sig-label">Date signed</div>
  </div>`;
}

function spousalLine(ctx: GivingBuildContext): string {
  if (!ctx.data.spousal_consent_needed) return '';
  return `<h2>Spousal consent</h2>
  <p>I am the spouse of the person giving. I know what is being given, I agree to it, and I give up any claim I have in it.</p>
  ${sigBlock('Spouse signature')}`;
}

/** Shape one: the thing stays in the giver's name. */
export function buildVehicleCommitmentHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>On ${esc(longDate(ctx.date))}, ${giver(ctx)} records this commitment with ${receiver(ctx)}.</p>
  <h2>The vehicle</h2>
  <p>${esc(data.vehicle_description || '[Year, make, and model]')}${
    data.vehicle_vin ? `, vehicle identification number ${esc(data.vehicle_vin)}` : ''
  }.</p>
  <h2>What this is</h2>
  <p>This vehicle is held in stewardship by commitment. The conviction is honored in full by this written record. Nothing else changes.</p>
  <ul>
    <li>Title stays in the personal name of the person giving.</li>
    <li>Insurance stays personal, in the same name as the title.</li>
    <li>Nothing changes at the DMV.</li>
    <li>Use of the vehicle for the work of the organization may be reimbursed under the written expense rules, and only under those rules.</li>
  </ul>
  <h2>How this ends</h2>
  <p>This commitment can be ended in writing at any time, and the ending is filed with the records the same way this page is.</p>
  ${sigBlock('Person making the commitment')}
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}`;
  return page('Vehicle Stewardship Commitment', 'A one page record of a commitment held in stewardship', inner);
}

/** Entity resolves to receive the vehicle. */
export function buildAcquiringResolutionHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>${receiver(ctx)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>What is being received</h2>
  <p>${esc(data.vehicle_description || '[Year, make, and model]')}${
    data.vehicle_vin ? `, vehicle identification number ${esc(data.vehicle_vin)}` : ''
  }, given by ${giver(ctx)}.</p>
  <h2>The action</h2>
  <ul>
    <li>The entity accepts the vehicle and takes title in its own exact legal name.</li>
    <li>The entity binds a commercial policy in its own exact legal name before the title moves.</li>
    <li>The entity carries the registration, the insurance, and the upkeep from the day title moves.</li>
    <li>The officer signing below is authorized to sign the title papers and anything the title office requires.</li>
  </ul>
  ${data.vehicle_driver ? `<h2>Who will drive it</h2><p>${esc(data.vehicle_driver)}, under the written use policy.</p>` : ''}
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Acquiring Resolution', 'The entity resolves to receive the vehicle', inner);
}

function givingRecordInner(ctx: GivingBuildContext, thingText: string): string {
  return `
  <p>On ${esc(longDate(ctx.date))}, ${giver(ctx)} gives the following into the stewardship of ${receiver(ctx)}.</p>
  <h2>What is given</h2>
  <p>${thingText}</p>
  <h2>How it is given</h2>
  <ul>
    <li>The giving is voluntary. Nobody asked for it as a condition of anything.</li>
    <li>The giving is irrevocable. Once it is done, it is done.</li>
    <li>The giving is not reciprocal. Nothing is promised back, and nothing is expected back.</li>
    <li>Nothing about this giving is contingent on any outcome of any kind.</li>
  </ul>
  <h2>Condition of the thing</h2>
  <p>It is given as it stands today, with anything owed on it disclosed in writing before this page is signed.</p>
  ${sigBlock('Person giving')}
  ${spousalLine(ctx)}
  ${sigBlock(`${ctx.associationOfficerRole}, receiving for ${ctx.associationName}`)}`;
}

export function buildVehicleGivingRecordHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const thing = `${esc(data.vehicle_description || '[Year, make, and model]')}${
    data.vehicle_vin ? `, vehicle identification number ${esc(data.vehicle_vin)}` : ''
  }.`;
  return page(
    'Giving Record',
    'A written record of a vehicle given into stewardship',
    `${givingRecordInner(ctx, thing)}${sealSpot(ctx)}`,
  );
}

export function buildPropertyGivingRecordHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const thing = `The real property at ${esc(data.property_address || '[Property address]')}${
    data.property_county ? `, in ${esc(data.property_county)} County` : ''
  }.`;
  return page(
    'Giving Record',
    'A written record of property given into stewardship',
    `${givingRecordInner(ctx, thing)}${sealSpot(ctx)}`,
  );
}

export function buildEquipmentGivingRecordHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const thing = `${esc(data.equipment_description || '[What it is]')}${
    data.equipment_value ? `. Stated value: ${esc(data.equipment_value)}, with the basis for that number attached.` : '.'
  }`;
  const inner = `${givingRecordInner(ctx, thing)}
  <h2>Where it is kept</h2>
  <p>${esc(data.equipment_custody || '[Where it is kept]')}</p>
  <p>Any storage account, vault account, safe deposit box, or storage unit holding this is in the exact legal name of
  the entity, and the entity controls it.</p>
  ${sealSpot(ctx)}`;
  return page('Giving Record', 'A written record of belongings given into stewardship', inner);
}

export function buildVehicleTitleChecklistHtml(ctx: GivingBuildContext): string {
  const inner = `
  <p>Work these in order. Write the answer on this page as you go, and file the page with the set.</p>
  <ol>
    <li>
      <strong>One call to the DMV or title office.</strong> Say this: "This vehicle is being given, not sold. What does
      this state charge on a gifted transfer, and is there an exemption form?" Write the answer here and file this page.
      <table><tr><td style="height:70px">Who you spoke with, what they said, and any form they named:</td></tr></table>
    </li>
    <li>
      <strong>The insurance call.</strong> A commercial policy in the entity's exact legal name has to be bound before
      the DMV visit, not after.
      <table><tr><td style="height:60px">Carrier, policy number, and the date it binds:</td></tr></table>
    </li>
    <li>
      <strong>The DMV visit.</strong> Bring the entity's identification number letter and its formation papers, along
      with the signed title and the giving record.
      <table><tr><td style="height:60px">Date of the visit and what came back:</td></tr></table>
    </li>
    <li>
      <strong>The use policy and the log.</strong> Sign the use policy resolution and start the use log the first day
      the vehicle is driven.
      <table><tr><td style="height:60px">Date the policy was signed and the log was started:</td></tr></table>
    </li>
  </ol>`;
  return page('Title Move Checklist', 'Four steps, in this order, for moving a vehicle title', inner);
}

export function buildPropertyTitleChecklistHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>Work these in order for the property at ${esc(data.property_address || '[Property address]')}. Write each answer
  on this page and file it with the set.</p>
  <ol>
    <li>
      <strong>A title company or an attorney prepares the deed.</strong> Nobody in the portal writes a deed.
      <table><tr><td style="height:60px">Who is preparing it, and the date:</td></tr></table>
    </li>
    <li>
      <strong>County transfer tax and reassessment get checked before recording, not after.</strong> Ask the county
      directly and write down what they say.
      <table><tr><td style="height:70px">What the county said:</td></tr></table>
    </li>
    <li>
      <strong>Landlord insurance in the company's exact legal name, bound before the first tenant.</strong>
      <table><tr><td style="height:60px">Carrier, policy number, and the date it binds:</td></tr></table>
    </li>
    <li>
      <strong>Record the deed, then file the recorded copy with this set.</strong>
      <table><tr><td style="height:60px">Recording date and document number:</td></tr></table>
    </li>
  </ol>`;
  return page('Title Move Checklist', 'Four steps, in this order, for moving property title', inner);
}

export function buildAuthorizingResolutionHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>${giver(ctx)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>The action</h2>
  <ul>
    <li>The property at ${esc(data.property_address || '[Property address]')} is given into the stewardship of ${receiver(
      ctx,
    )}.</li>
    <li>The giving is voluntary, irrevocable, and not reciprocal.</li>
    <li>The person signing below is authorized to sign the deed and everything the county requires.</li>
    <li>Nothing is signed and nothing is recorded until the checklist steps are done in order.</li>
  </ul>
  ${sigBlock('Person giving')}
  ${spousalLine(ctx)}
  ${sealSpot(ctx)}`;
  return page('Authorizing Resolution', 'Authorizing the giving of property', inner);
}

export function buildAcceptingResolutionHtml(ctx: GivingBuildContext, thingText: string): string {
  const inner = `
  <p>${receiver(ctx)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>What is accepted</h2>
  <p>${thingText}</p>
  <h2>The action</h2>
  <ul>
    <li>The entity accepts what is described above and takes it in its own exact legal name.</li>
    <li>The entity carries the cost, the care, and the insurance from the day it is received.</li>
    <li>The officer signing below is authorized to sign whatever the transfer requires.</li>
    <li>This page and the giving record are filed together in the records.</li>
  </ul>
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Accepting Resolution', 'The entity resolves to accept what is given', inner);
}

export function buildDesignationResolutionHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>${receiver(ctx)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>The place</h2>
  <p>${esc(data.property_address || '[Property address]')}.</p>
  <h2>The action</h2>
  <ul>
    <li>The place named above is designated a place of stewardship observance.</li>
    <li>The place is used for the gatherings, the study, and the work of the organization.</li>
    <li>A use log is kept for the place, and each gathering gets its one line.</li>
    <li>This designation stands until it is changed in writing.</li>
  </ul>
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Designation Resolution', 'Designating a place of stewardship observance', inner);
}

export function buildVehicleUsePolicyResolutionHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>${receiver(ctx)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>The vehicle</h2>
  <p>${esc(data.vehicle_description || '[Year, make, and model]')}${
    data.vehicle_vin ? `, vehicle identification number ${esc(data.vehicle_vin)}` : ''
  }.</p>
  <h2>The policy</h2>
  <ul>
    <li>The vehicle is for the work of the organization.</li>
    <li>Only people named by the entity drive it, and each of them holds a valid license.</li>
    <li>Every trip gets a line in the use log: the date, where it went, why, and the miles.</li>
    <li>Personal use is not the purpose of this vehicle. If any personal use happens, it is written in the log and handled under the written expense rules.</li>
    <li>Fuel, upkeep, and insurance are paid by the entity from its own account.</li>
  </ul>
  ${data.vehicle_driver ? `<h2>People named to drive</h2><p>${esc(data.vehicle_driver)}</p>` : ''}
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Vehicle Use Policy Resolution', 'How the vehicle is used and who drives it', inner);
}

export function buildUseLogHtml(ctx: GivingBuildContext): string {
  const rows = Array.from({ length: 20 })
    .map(() => '<tr><td style="height:30px"></td><td></td><td></td><td></td><td></td></tr>')
    .join('');
  const inner = `
  <p>One line per trip. Fill it in the same week it happens, not at the end of the year.</p>
  <table>
    <thead><tr><th>Date</th><th>Driver</th><th>Where it went</th><th>Why</th><th>Miles</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
  return page('Vehicle Use Log', 'A blank log to start the day the vehicle is first driven', inner);
}

export function buildEncumbranceWorksheetHtml(ctx: GivingBuildContext): string {
  const rows = ENCUMBRANCE_LINES.map(
    (line, i) =>
      `<tr><td style="width:28px">${i + 1}</td><td>${esc(line)}</td><td style="height:56px">What you found:</td></tr>`,
  ).join('');
  const inner = `
  <p>Work this through before anything assigns. Finding these after the fact is the expensive order. Bring the finished
  page to the team.</p>
  <table><thead><tr><th>#</th><th>What to check</th><th>Finding</th></tr></thead><tbody>${rows}</tbody></table>
  <h2>Who filled this out</h2>
  ${sigBlock('Owner')}`;
  return page('Encumbrance Review Worksheet', 'Thirteen things to check before a company moves', inner);
}

export function buildSecuritiesGivingRecordHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const thing = `${esc(data.securities_description || '[The holdings being given]')}${
    data.securities_institution ? `, held at ${esc(data.securities_institution)}` : ''
  }.`;
  const inner = `${givingRecordInner(ctx, thing)}
  <h2>How the entity received it</h2>
  <p>The entity received the position in kind, meaning the holdings themselves moved into the investment account of the
  entity. Nothing was sold to make this giving happen.</p>
  <table>
    <tr><th>Transfer date</th><td style="height:34px"></td></tr>
    <tr><th>Receiving account, in the exact legal name of the entity</th><td style="height:34px"></td></tr>
    <tr><th>Shares or units received</th><td style="height:34px"></td></tr>
  </table>
  <p class="note">Whether and how this shows up on your own return belongs to your licensed professional.</p>
  ${sealSpot(ctx)}`;
  return page('Giving Record', 'A written record of holdings given into stewardship', inner);
}

export function buildSecuritiesTransferChecklistHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const inner = `
  <p>Work these in order. Write each answer on this page and file it with the set.</p>
  <ol>
    <li>
      <strong>The sequencing check.</strong> Nothing on this list happens if any sale of these holdings is planned or in
      motion. Talk with the team and your licensed professional first.
      <table><tr><td style="height:60px">Confirmed that no sale is planned or in motion, and by whom:</td></tr></table>
    </li>
    <li>
      <strong>The broker call.</strong> Ask ${esc(
        data.securities_institution || '[the institution holding the account]',
      )} for their charitable or gift transfer form and their process for it.
      <table><tr><td style="height:70px">Who you spoke with, the form they named, and what they require:</td></tr></table>
    </li>
    <li>
      <strong>The transfer.</strong> The holdings transfer into the investment account of the entity, in the exact legal
      name of the entity.
      <table><tr><td style="height:60px">Date of the transfer and the receiving account:</td></tr></table>
    </li>
    <li>
      <strong>The confirmation.</strong> File the confirmation statement with this record.
      <table><tr><td style="height:60px">Statement date and where it is filed:</td></tr></table>
    </li>
  </ol>
  <p class="note">Whether and how this shows up on your own return belongs to your licensed professional.</p>`;
  return page('Securities Transfer Checklist', 'Four steps, in this order, for moving holdings', inner);
}

export function buildCryptoGivingRecordHtml(ctx: GivingBuildContext): string {
  const { data } = ctx;
  const thing = `${esc(data.crypto_description || '[The coins and the amount being given]')}.`;
  const inner = `${givingRecordInner(ctx, thing)}
  <h2>The move itself</h2>
  <p>Write these in after the transfer settles, then file this page.</p>
  <table>
    <tr><th>Transaction identifier</th><td style="height:34px"></td></tr>
    <tr><th>Date and time of the transfer</th><td style="height:34px"></td></tr>
    <tr><th>Fair value at the time of transfer, and the source of that number</th><td style="height:44px"></td></tr>
  </table>
  <p class="note">Seed phrases never appear in this record or in any other record. Nobody shares a seed, ever.</p>
  ${sealSpot(ctx)}`;
  return page('Giving Record', 'A written record of digital assets given into stewardship', inner);
}

export function buildCryptoTransferChecklistHtml(_ctx: GivingBuildContext): string {
  const inner = `
  <p>Work these in order. Write each answer on this page and file it with the set.</p>
  <ol>
    <li>
      <strong>Verify the wallet address with a test amount first.</strong> Send a small amount, confirm it landed in the
      wallet of the entity, and only then continue.
      <table><tr><td style="height:60px">Test amount, date, and confirmation:</td></tr></table>
    </li>
    <li>
      <strong>Send the full transfer.</strong>
      <table><tr><td style="height:60px">Amount and date:</td></tr></table>
    </li>
    <li>
      <strong>Write the transaction identifier and the date onto the giving record.</strong>
      <table><tr><td style="height:60px">Written in on this date:</td></tr></table>
    </li>
    <li>
      <strong>Journal the receipt on the books of the entity.</strong>
      <table><tr><td style="height:60px">Date journaled and by whom:</td></tr></table>
    </li>
  </ol>
  <p class="note">Seed phrases never appear on this page or in any record.</p>`;
  return page('Transfer Checklist', 'Four steps, in this order, for moving digital assets', inner);
}

function buildGivingOutputsInternal(ctx: GivingBuildContext): GivingOutput[] {
  const { data } = ctx;
  const outputs: GivingOutput[] = [];

  if (data.storm !== 'no') return outputs;

  if (data.thing === 'vehicle') {
    if (data.vehicle_privacy_pattern) return outputs;
    if (data.vehicle_job === 'family') {
      outputs.push({
        key: 'giving_vehicle_commitment',
        title: 'Vehicle Stewardship Commitment',
        html: buildVehicleCommitmentHtml(ctx),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: false,
      });
      return outputs;
    }
    if (data.vehicle_job === 'mission') {
      outputs.push(
        {
          key: 'giving_acquiring_resolution',
          title: 'Acquiring Resolution',
          html: buildAcquiringResolutionHtml(ctx),
          requiresSignature: true,
          signing: 'stewards_choice',
        defaultInk: true,
        },
        {
          key: 'giving_record_vehicle',
          title: 'Giving Record',
          html: buildVehicleGivingRecordHtml(ctx),
          requiresSignature: true,
          signing: 'stewards_choice',
        defaultInk: true,
        },
        {
          key: 'giving_title_checklist_vehicle',
          title: 'Title Move Checklist',
          html: buildVehicleTitleChecklistHtml(ctx),
          requiresSignature: false,
          signing: 'stewards_choice',
        defaultInk: false,
        },
        {
          key: 'giving_vehicle_use_policy',
          title: 'Vehicle Use Policy Resolution',
          html: buildVehicleUsePolicyResolutionHtml(ctx),
          requiresSignature: true,
          signing: 'stewards_choice',
        defaultInk: false,
        },
        {
          key: 'giving_vehicle_use_log',
          title: 'Vehicle Use Log',
          html: buildUseLogHtml(ctx),
          requiresSignature: false,
          signing: 'stewards_choice',
        defaultInk: false,
        },
      );
      return outputs;
    }
    return outputs;
  }

  if (data.thing === 'home_land') {
    if (data.land_lived_in === 'yes') return outputs;
    if (data.land_use === 'rental' && !data.land_llc_exists) return outputs;
    if (data.land_use !== 'rental' && data.land_use !== 'mission') return outputs;

    const thing = `The real property at ${esc(data.property_address || '[Property address]')}${
      data.property_county ? `, in ${esc(data.property_county)} County` : ''
    }.`;
    outputs.push(
      {
        key: 'giving_record_property',
        title: 'Giving Record',
        html: buildPropertyGivingRecordHtml(ctx),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_authorizing_resolution',
        title: 'Authorizing Resolution',
        html: buildAuthorizingResolutionHtml(ctx),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_accepting_resolution_property',
        title: 'Accepting Resolution',
        html: buildAcceptingResolutionHtml(ctx, thing),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_title_checklist_property',
        title: 'Title Move Checklist',
        html: buildPropertyTitleChecklistHtml(ctx),
        requiresSignature: false,
        signing: 'stewards_choice',
        defaultInk: false,
      },
    );
    if (data.land_use === 'mission') {
      outputs.push({
        key: 'giving_designation_resolution',
        title: 'Designation Resolution',
        html: buildDesignationResolutionHtml(ctx),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      });
    }
    return outputs;
  }

  if (data.thing === 'life_insurance') {
    return outputs;
  }

  if (data.thing === 'securities') {
    if (data.securities_entity_account_exists !== 'yes') return outputs;
    const thing = `${esc(data.securities_description || '[The holdings being given]')}${
      data.securities_institution ? `, held at ${esc(data.securities_institution)}` : ''
    }.`;
    outputs.push(
      {
        key: 'giving_record_securities',
        title: 'Giving Record',
        html: buildSecuritiesGivingRecordHtml(ctx),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_accepting_resolution_securities',
        title: 'Accepting Resolution',
        html: buildAcceptingResolutionHtml(ctx, thing),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_securities_transfer_checklist',
        title: 'Securities Transfer Checklist',
        html: buildSecuritiesTransferChecklistHtml(ctx),
        requiresSignature: false,
        signing: 'stewards_choice',
        defaultInk: false,
      },
    );
    return outputs;
  }

  if (data.thing === 'crypto') {
    if (data.crypto_entity_wallet_exists !== 'yes') return outputs;
    const thing = `${esc(data.crypto_description || '[The coins and the amount being given]')}.`;
    outputs.push(
      {
        key: 'giving_record_crypto',
        title: 'Giving Record',
        html: buildCryptoGivingRecordHtml(ctx),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_accepting_resolution_crypto',
        title: 'Accepting Resolution',
        html: buildAcceptingResolutionHtml(ctx, thing),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_crypto_transfer_checklist',
        title: 'Transfer Checklist',
        html: buildCryptoTransferChecklistHtml(ctx),
        requiresSignature: false,
        signing: 'stewards_choice',
        defaultInk: false,
      },
    );
    return outputs;
  }

  if (data.thing === 'business') {
    outputs.push({
      key: 'giving_encumbrance_worksheet',
      title: 'Encumbrance Review Worksheet',
      html: buildEncumbranceWorksheetHtml(ctx),
      requiresSignature: false,
      signing: 'stewards_choice',
        defaultInk: false,
    });
    return outputs;
  }

  if (data.thing === 'equipment') {
    const thing = `${esc(data.equipment_description || '[What it is]')}${
      data.equipment_value ? `. Stated value: ${esc(data.equipment_value)}.` : '.'
    }`;
    outputs.push(
      {
        key: 'giving_record_equipment',
        title: 'Giving Record',
        html: buildEquipmentGivingRecordHtml(ctx),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
      {
        key: 'giving_accepting_resolution_equipment',
        title: 'Accepting Resolution',
        html: buildAcceptingResolutionHtml(ctx, thing),
        requiresSignature: true,
        signing: 'stewards_choice',
        defaultInk: true,
      },
    );
    return outputs;
  }

  return outputs;
}

/**
 * Every generated paper carries only the entity's identity. The HTML title
 * becomes "Document title, Entity name" so the file and the PDF metadata name
 * the Association, not the portal.
 */
export function buildGivingOutputs(ctx: GivingBuildContext): GivingOutput[] {
  const entityName = ctx.associationName;
  return buildGivingOutputsInternal(ctx).map((output) => ({ ...output, html: withEntityTitle(output.html, entityName) }));
}
